import { create } from "zustand";
import { ReportDetailMutations, ReportDetailState } from "./interface";
import { REPORT_DETAIL_INITIAL_STATE } from "./state";
import { createDataActions } from "./actions/dataActions";
import { createUIActions } from "./actions/uiActions";
import { createUpdateActions } from "./actions/updateActions";
import { createAutoSaveActions } from "./actions/autoSaveActions";
import { createDerivedActions } from "./actions/derivedActions";
import { createMergeActions } from "./actions/mergeActions";

export const useReportDetailStore = create<ReportDetailState & { actions: ReportDetailMutations }>()(
  (set, get) => {
    const dataActions = createDataActions(set, get, undefined as any);
    const uiActions = createUIActions(set, get, undefined as any);
    const updateActions = createUpdateActions(set, get, undefined as any);
    const autoSaveActions = createAutoSaveActions(set, get, undefined as any);
    const derivedActions = createDerivedActions(set, get, undefined as any);
    const mergeActions = createMergeActions(set, get, undefined as any);

    // Sobrescreve setReportSections e setDeletedSections para recalcular automaticamente
    const enhancedDataActions = {
      ...dataActions,
      setReportSections: (sections: any) => {
        dataActions.setReportSections(sections);
        derivedActions.recalculateSectionList();
      },
      setDeletedSections: (deletedSections: any) => {
        dataActions.setDeletedSections(deletedSections);
        derivedActions.recalculateSectionList();
      },
      setSelectedCombinedFilter: (filterId: string) => {
        dataActions.setSelectedCombinedFilter(filterId);
        derivedActions.recalculateSectionList();
      },
    };

    return {
      ...REPORT_DETAIL_INITIAL_STATE,
      actions: {
        ...enhancedDataActions,
        ...uiActions,
        ...updateActions,
        ...autoSaveActions,
        ...derivedActions,
        ...mergeActions,
        resetReportDetailStore: () => set(() => REPORT_DETAIL_INITIAL_STATE),
        resetRelacoesState: () =>
          set((state) => ({
            ui: {
              ...state.ui,
              activeRelacaoTab: REPORT_DETAIL_INITIAL_STATE.ui.activeRelacaoTab,
              highlightedEntityId: REPORT_DETAIL_INITIAL_STATE.ui.highlightedEntityId,
            },
          })),

        setSummaryData: (summaryData) => {
          const sectionsWithSummary: string[] = [];
          if (summaryData?.topics) {
            const sectionsSet = new Set<string>();
            summaryData.topics.forEach((topic) => {
              if (topic?.related_sections && Array.isArray(topic?.related_sections)) {
                topic.related_sections.forEach((section) => {
                  sectionsSet.add(section);
                });
              }
            });
            sectionsWithSummary.push(...Array.from(sectionsSet));
          }
          set((state) => ({
            data: { ...state.data, summaryData, sectionsWithSummary },
          }));
        },

        setSummaryStatus: (summaryStatus) =>
          set((state) => ({
            data: { ...state.data, summaryStatus },
          })),

        setSummaryErrorMessage: (summaryErrorMessage) =>
          set((state) => ({
            data: { ...state.data, summaryErrorMessage },
          })),

        setIsGeneratingSummary: (reportId, isGenerating) =>
          set((state) => {
            const current = state.ui.generatingSummaryReportIds;
            const next = isGenerating
              ? current.includes(reportId)
                ? current
                : [...current, reportId]
              : current.filter((rid) => rid !== reportId);
            return { ui: { ...state.ui, generatingSummaryReportIds: next } };
          }),
      },
    };
  });